import { errorMessage, phoneSchema } from "@workspace/domain/src/utils";
import * as z from "zod";

const SignInSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("google"),
    data: z.object({
      token: z.string(),
    }),
  }),
  z.object({
    type: z.literal("apple"),
    data: z.object({
      token: z.string(),
      nonce: z.string(),
      email: z.string(),
      name: z.string(),
    }),
  }),
]);

const AuthDataSchema = z.object({
  session: z.object({
    id: z.string(),
    userType: z.enum(["CLIENT"]),
    secretHash: z.instanceof(Uint8Array),
    expiresAt: z.date(),
    userId: z.string(),
  }),
  userType: z.literal("CLIENT"),
  user: z.object({
    id: z.string(),
    providerId: z.string(),
    name: z.string(),
    email: z.string().nullable(),
    imageUrl: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    phoneNumberVerified: z.boolean(),
  }),
});

const UpdateProfileSchema = z.object({
  name: z
    .string(errorMessage("Name is required", "Name is invalid"))
    .nonempty("Name is required"),
  phoneNumber: z
    .union([
      phoneSchema("Phone number is required", "Phone number is invalid"),
      z.literal(""),
    ])
    .optional(),
});

const UpdateProfileImageSchema = z.object({
  image: z
    .instanceof(File)
    .refine(
      (file) => ["image/jpeg", "image/png", "image/webp"].includes(file.type),
      {
        message: "Image must be a JPEG, PNG, or WebP",
      },
    )
    .refine(
      (file) => file.size <= 10 * 1024 ** 2,
      "Image must be less than 10MB",
    )
    .optional(),
});

// National ID schemas (E2E encrypted)
const EncryptedKeySchema = z.object({
  agencyId: z.string(),
  encryptedKey: z.string(),
});

const UpdateNIdSchema = z.object({
  frontImage: z
    .instanceof(File)
    .refine(
      (file) => file.type === "application/octet-stream",
      "Front image must be encrypted binary data",
    )
    .refine(
      (file) => file.size <= 15 * 1024 ** 2,
      "Front image must be less than 15MB",
    ),
  frontIv: z.instanceof(File),
  backImage: z
    .instanceof(File)
    .refine(
      (file) => file.type === "application/octet-stream",
      "Back image must be encrypted binary data",
    )
    .refine(
      (file) => file.size <= 15 * 1024 ** 2,
      "Back image must be less than 15MB",
    ),
  backIv: z.instanceof(File),
  encryptedKeys: z.array(EncryptedKeySchema).optional(),
});

const GetAgenciesForNIdSharingSchema = z.array(
  z.object({
    agencyId: z.string(),
    publicKey: z.string(),
  }),
);

const GetNIdSchema = z
  .object({
    frontImageUrl: z.string(),
    backImageUrl: z.string(),
    frontIvB64: z.string(),
    backIvB64: z.string(),
  })
  .nullable();

export const ClientAuth = {
  SignIn: SignInSchema,
  AuthData: AuthDataSchema,
  UpdateProfile: UpdateProfileSchema,
  UpdateProfileImage: UpdateProfileImageSchema,
  UpdateNId: UpdateNIdSchema,
  getNId: GetNIdSchema,
  GetAgenciesForNIdSharing: GetAgenciesForNIdSharingSchema,
  EncryptedKey: EncryptedKeySchema,
};
export type ClientAuth = {
  SignIn: z.infer<typeof SignInSchema>;
  AuthData: z.infer<typeof AuthDataSchema>;
  UpdateProfile: z.infer<typeof UpdateProfileSchema>;
  UpdateProfileImage: z.infer<typeof UpdateProfileImageSchema>;
  UpdateNId: z.infer<typeof UpdateNIdSchema>;
  getNId: z.infer<typeof GetNIdSchema>;
  GetAgenciesForNIdSharing: z.infer<typeof GetAgenciesForNIdSharingSchema>;
  EncryptedKey: z.infer<typeof EncryptedKeySchema>;
};
